import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { authUrl } from '../utils/api';
import Skeleton from './common/Skeleton';
import './AnalysisOverview.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function calcRiskScore(vulnData, licenseData, qualityData) {
  let score = 100;

  if (vulnData) {
    const critical = vulnData.critical || 0;
    const high = vulnData.high || 0;
    score -= Math.min(critical * 20, 60);
    score -= Math.min(high * 5, 30);
  }

  if (licenseData) {
    const risky = (licenseData.licenses || []).filter(
      l => l.risk === 'STRONG_COPYLEFT' || l.risk === 'WEAK_COPYLEFT'
    ).length;
    score -= risky * 3;
  }

  if (qualityData) {
    const grade = qualityData.grade;
    const penalty = { A: 0, B: -5, C: -10, D: -20, F: -30 };
    score += (penalty[grade] ?? -10);
  }

  return Math.max(0, Math.min(100, score));
}

function riskColor(score) {
  if (score > 70) return 'green';
  if (score >= 40) return 'amber';
  return 'red';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PipelineStep({ label, status, errorCount }) {
  return (
    <span className={`ao-pipeline-step ${status}`} title={status === 'error' && errorCount ? errorCount : undefined}>
      {status === 'loading' && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
      {status === 'success' && '✓'}
      {status === 'error' && '✗'}
      {label}
      {status === 'error' && errorCount != null && (
        <span className="ao-badge-count">{errorCount}</span>
      )}
    </span>
  );
}

const SEV_KEYS = { CRITICAL: 'vulnerabilities.critical', HIGH: 'vulnerabilities.high', MEDIUM: 'vulnerabilities.medium', LOW: 'vulnerabilities.low' };

function SecurityCard({ data, loading, onNavigate }) {
  const { t } = useTranslation();
  return (
    <div className="ao-card">
      <div className="ao-card-header">🛡️ {t('analysis.security')}</div>
      <div className="ao-card-body">
        {loading ? (
          <Skeleton.Text lines={3} lastWidth="70%" />
        ) : data ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
            {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(sev => (
              <span key={sev} className={`ce-sev ce-sev--${sev.toLowerCase()}`}>
                {data[sev.toLowerCase()] ?? 0} {t(SEV_KEYS[sev])}
              </span>
            ))}
            <div className="ao-row" style={{ width: '100%', marginTop: '0.25rem' }}>
              <span className="ao-row-label">{t('analysis.scannedPackages')}</span>
              <span className="ao-row-value">{data.scannedPackages ?? '—'}</span>
            </div>
            <div className="ao-row" style={{ width: '100%' }}>
              <span className="ao-row-label">{t('analysis.totalVulns')}</span>
              <span className="ao-row-value">{data.totalVulns ?? 0}</span>
            </div>
          </div>
        ) : (
          <span className="text-secondary fst-italic">{t('common.noData')}</span>
        )}
      </div>
      <div className="ao-card-footer">
        <button className="ao-link" onClick={() => onNavigate('security')}>
          {t('analysis.viewDetails')}
        </button>
      </div>
    </div>
  );
}

function HealthCard({ data, loading, onNavigate }) {
  const { t } = useTranslation();
  return (
    <div className="ao-card">
      <div className="ao-card-header">🏥 {t('analysis.health')}</div>
      <div className="ao-card-body">
        {loading ? (
          <Skeleton.Text lines={3} lastWidth="65%" />
        ) : data ? (
          <>
            <div className="ao-row">
              <span className="ao-row-label">{t('health.avgScore')}</span>
              <span className="ao-row-value">{data.avgScorecardScore != null ? data.avgScorecardScore.toFixed(1) : '—'}</span>
            </div>
            <div className="ao-row">
              <span className="ao-row-label">{t('analysis.outdated')}</span>
              <span className="ao-row-value">
                {data.deprecatedCount != null
                  ? `${data.deprecatedCount} (${data.total ? Math.round((data.deprecatedCount / data.total) * 100) : 0}%)`
                  : '—'}
              </span>
            </div>
            <div className="ao-row">
              <span className="ao-row-label">{t('analysis.abandoned')}</span>
              <span className="ao-row-value">{data.abandonedCount ?? '—'}</span>
            </div>
            <div className="ao-row">
              <span className="ao-row-label">{t('health.totalPackages')}</span>
              <span className="ao-row-value">{data.total ?? '—'}</span>
            </div>
          </>
        ) : (
          <span className="text-secondary fst-italic">{t('common.noData')}</span>
        )}
      </div>
      <div className="ao-card-footer">
        <button className="ao-link" onClick={() => onNavigate('health')}>
          {t('analysis.viewDetails')}
        </button>
      </div>
    </div>
  );
}

function LicenseCard({ data, loading, onNavigate }) {
  const { t } = useTranslation();
  const riskyCount = data
    ? (data.licenses || []).filter(l => l.risk === 'STRONG_COPYLEFT' || l.risk === 'WEAK_COPYLEFT').length
    : 0;
  const topLicenses = data ? (data.licenses || []).slice(0, 3).map(l => l.spdxId || l.name).filter(Boolean) : [];

  return (
    <div className="ao-card">
      <div className="ao-card-header">⚖️ {t('analysis.licenses')}</div>
      <div className="ao-card-body">
        {loading ? (
          <Skeleton.Text lines={3} lastWidth="40%" />
        ) : data ? (
          <>
            <div className="ao-row">
              <span className="ao-row-label">{t('licenses.uniqueLicenses')}</span>
              <span className="ao-row-value">{(data.licenses || []).length}</span>
            </div>
            <div className="ao-row">
              <span className="ao-row-label">{t('analysis.licenseRisk')}</span>
              <span className="ao-row-value" style={{ color: riskyCount > 0 ? '#ef4444' : 'inherit' }}>
                {riskyCount}
              </span>
            </div>
            {topLicenses.length > 0 && (
              <div className="ao-row" style={{ flexWrap: 'wrap', gap: '0.25rem' }}>
                <span className="ao-row-label" style={{ width: '100%' }}>{t('licenses.mostCommon')}</span>
                {topLicenses.map(l => (
                  <span key={l} className="ce-tag">{l}</span>
                ))}
              </div>
            )}
          </>
        ) : (
          <span className="text-secondary fst-italic">{t('common.noData')}</span>
        )}
      </div>
      <div className="ao-card-footer">
        <button className="ao-link" onClick={() => onNavigate('licenses')}>
          {t('analysis.viewDetails')}
        </button>
      </div>
    </div>
  );
}

function QualityCard({ data, loading, onNavigate }) {
  const { t } = useTranslation();
  const gradeClass = data ? `ao-grade-${data.grade}` : '';
  return (
    <div className="ao-card">
      <div className="ao-card-header">🏆 {t('analysis.quality')}</div>
      <div className="ao-card-body">
        {loading ? (
          <Skeleton.Text lines={2} lastWidth="50%" />
        ) : data ? (
          <>
            <div className="ao-row">
              <span className="ao-row-label">{t('quality.grade')}</span>
              <span className={`ao-grade ${gradeClass}`}>{data.grade ?? '—'}</span>
            </div>
            <div className="ao-row">
              <span className="ao-row-label">{t('quality.overall')}</span>
              <span className="ao-row-value">{data.score != null ? `${data.score}%` : '—'}</span>
            </div>
            {data.dimensions && data.dimensions.length > 0 && (
              <div className="ao-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.2rem' }}>
                <span className="ao-row-label" style={{ marginBottom: '0.2rem' }}>{t('quality.dimensions')}</span>
                {data.dimensions.slice(0, 3).map(d => (
                  <div key={d.name} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '0.8rem' }}>
                    <span>{d.name}</span>
                    <span style={{ fontWeight: 600 }}>{d.score}%</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <span className="text-secondary fst-italic">{t('common.noData')}</span>
        )}
      </div>
      <div className="ao-card-footer">
        <button className="ao-link" onClick={() => onNavigate('quality')}>
          {t('analysis.viewDetails')}
        </button>
      </div>
    </div>
  );
}

function ComplianceCard({ data, loading, onNavigate }) {
  const { t } = useTranslation();
  const frameworks = ['NIST SP 800-53', 'ISO 27001', 'CRA'];
  return (
    <div className="ao-card">
      <div className="ao-card-header">🏛️ {t('analysis.compliance')}</div>
      <div className="ao-card-body">
        {loading ? (
          <Skeleton.Text lines={2} lastWidth="60%" />
        ) : data ? (
          <>
            <div className="ao-row">
              <span className="ao-row-label">{t('compliance.mappedControlsLabel')}</span>
              <span className="ao-row-value">{data.mappedControls ?? (data.controls?.length ?? '—')}</span>
            </div>
            <div className="ao-row" style={{ flexWrap: 'wrap', gap: '0.25rem' }}>
              <span className="ao-row-label" style={{ width: '100%' }}>{t('analysis.frameworks')}</span>
              {frameworks.map(f => (
                <span key={f} className="ce-tag">{f}</span>
              ))}
            </div>
          </>
        ) : (
          <span className="text-secondary fst-italic">{t('common.noData')}</span>
        )}
      </div>
      <div className="ao-card-footer">
        <button className="ao-link" onClick={() => onNavigate('compliance')}>
          {t('analysis.viewDetails')}
        </button>
      </div>
    </div>
  );
}

function RemediationCard({ data, loading, onNavigate }) {
  const { t } = useTranslation();
  if (loading) return (
    <div className="ao-card">
      <div className="ao-card-header">🔧 {t('analysis.remediation', 'Remediation')}</div>
      <div className="ao-card-body"><Skeleton.Text lines={3} lastWidth="70%" /></div>
    </div>
  );
  if (!data) return null;
  const items = Array.isArray(data) ? data : [];
  const closed = ['Fixed', 'Resolved', 'Suppressed', 'Not Affected'];
  const open = items.filter(i => !closed.includes(i.status));
  const overdue = open.filter(i => i.dueDate && new Date(i.dueDate) < new Date());
  const suppressed = items.filter(i => i.status === 'Suppressed' || i.status === 'Not Affected');
  return (
    <div className="ao-card">
      <div className="ao-card-header">🔧 {t('analysis.remediation', 'Remediation')}</div>
      <div className="ao-card-body">
        <div className="ao-row">
          <span className="ao-row-label">Open items</span>
          <span className="ao-row-value">{open.length}</span>
        </div>
        <div className="ao-row">
          <span className="ao-row-label">Overdue</span>
          <span className="ao-row-value" style={{ color: overdue.length > 0 ? '#dc2626' : undefined }}>{overdue.length}</span>
        </div>
        <div className="ao-row">
          <span className="ao-row-label">Suppressed</span>
          <span className="ao-row-value">{suppressed.length}</span>
        </div>
        {items.length === 0 && <div style={{ color: 'var(--bs-secondary-color)', fontSize: '0.8rem' }}>No remediation items tracked yet.</div>}
      </div>
      <div className="ao-card-footer">
        <button className="ce-btn ce-btn--secondary" style={{ fontSize: '0.8rem' }} onClick={() => onNavigate('remediation')}>
          {t('analysis.viewRemediation', 'View Remediation')} →
        </button>
      </div>
    </div>
  );
}

function QuickActionsCard({ repoName, sbomId, onNavigate }) {
  const { t } = useTranslation();
  const reportUrl = authUrl(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${sbomId}-sbom.json/report`);
  const csvUrl = authUrl(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${sbomId}-sbom.json/export/vulnerabilities`);

  return (
    <div className="ao-card">
      <div className="ao-card-header">⚡ {t('analysis.quickActions')}</div>
      <div className="ao-card-body">
        <div className="ao-actions">
          <a
            href={reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ce-btn ce-btn--primary"
          >
            📄 {t('analysis.downloadPdf')}
          </a>
          <a
            href={csvUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ce-btn ce-btn--secondary"
          >
            📥 {t('analysis.exportCsv')}
          </a>
          <button
            className="ce-btn ce-btn--secondary"
            onClick={() => onNavigate('policy')}
          >
            📋 {t('analysis.runPolicy')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

function AnalysisOverview({ repoName, sbomId, onNavigateTab, preloadedData }) {
  const { t } = useTranslation();
  const [states, setStates] = useState({
    health:     { loading: true, data: null, error: null },
    vulns:      { loading: true, data: null, error: null },
    licenses:   { loading: true, data: null, error: null },
    quality:    { loading: true, data: null, error: null },
    compliance: { loading: true, data: null, error: null },
  });
  const [sourceType, setSourceType] = useState(null);
  const [sourceValue, setSourceValue] = useState(null);

  const runAll = useCallback(() => {
    if (!repoName || !sbomId) return;

    setStates({
      health:     { loading: true, data: null, error: null },
      vulns:      { loading: true, data: null, error: null },
      licenses:   { loading: true, data: null, error: null },
      quality:    { loading: true, data: null, error: null },
      compliance: { loading: true, data: null, error: null },
    });

    const base = `${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${sbomId}-sbom.json`;

    const endpoints = {
      health:     `${base}/health`,
      vulns:      `${base}/vulnerabilities`,
      licenses:   `${base}/licenses`,
      quality:    `${base}/quality`,
      compliance: `${base}/compliance`,
    };

    const keys = Object.keys(endpoints);
    const requests = keys.map(k => axios.get(endpoints[k]));

    Promise.allSettled(requests).then(results => {
      const next = {};
      keys.forEach((k, i) => {
        const r = results[i];
        next[k] = {
          loading: false,
          data: r.status === 'fulfilled' ? r.value.data : null,
          error: r.status === 'rejected' ? (r.reason?.response?.data?.error || r.reason?.message || 'Failed to load') : null,
        };
      });
      setStates(next);
    });
  }, [repoName, sbomId]);

  useEffect(() => {
    if (preloadedData) return; // parent manages data — don't auto-fetch
    runAll();
  }, [runAll, preloadedData]);

  // Sync states from parent-provided preloadedData
  useEffect(() => {
    if (!preloadedData) return;
    const { vulns, health, licenses, quality, compliance } = preloadedData;
    const loading = (k) => preloadedData[k] === null;
    setStates({
      vulns:      { loading: loading('vulns'),      data: vulns,      error: null },
      health:     { loading: loading('health'),     data: health,     error: null },
      licenses:   { loading: loading('licenses'),   data: licenses,   error: null },
      quality:    { loading: loading('quality'),    data: quality,    error: null },
      compliance: { loading: loading('compliance'), data: compliance, error: null },
    });
  }, [preloadedData]);

  // Fetch SBOM list to determine sourceType/sourceValue for the selected SBOM
  useEffect(() => {
    if (!repoName || !sbomId) return;
    let cancelled = false;
    axios.get(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/list`)
      .then(res => {
        if (cancelled) return;
        const list = res.data || [];
        const entry = list.find(s => (s.commitHash || s.CommitHash) === sbomId);
        if (entry) {
          setSourceType(entry.sourceType || entry.SourceType || null);
          setSourceValue(entry.sourceValue || entry.SourceValue || null);
        }
      })
      .catch(() => {/* best effort */});
    return () => { cancelled = true; };
  }, [repoName, sbomId]);

  const navigate = useCallback((tab) => {
    if (onNavigateTab) onNavigateTab(tab);
  }, [onNavigateTab]);

  // Remediation data comes from parent preloadedData
  const remediationData = preloadedData?.remediation ?? null;
  const remediationLoading = preloadedData ? preloadedData.remediation === null : true;

  // ── Pipeline statuses ──
  const pipelineSteps = [
    { key: 'health',     label: t('analysis.health') },
    { key: 'vulns',      label: t('vulnerabilities.title') },
    { key: 'licenses',   label: t('analysis.licenses') },
    { key: 'quality',    label: t('analysis.quality') },
    { key: 'compliance', label: t('analysis.compliance') },
  ];

  function stepStatus(key) {
    const s = states[key];
    if (s.loading) return 'loading';
    if (s.error)   return 'error';
    return 'success';
  }

  // ── Risk score ──
  const allDone = Object.values(states).every(s => !s.loading);
  const riskScore = allDone
    ? calcRiskScore(states.vulns.data, states.licenses.data, states.quality.data)
    : null;
  const color = riskScore != null ? riskColor(riskScore) : 'amber';
  const failedSteps = pipelineSteps
    .map(step => ({ ...step, error: states[step.key]?.error }))
    .filter(step => step.error);

  const SOURCE_META = {
    'docker-image': { icon: '🐳', label: 'Docker Image' },
    'helm-chart':   { icon: '☸️', label: 'Helm Chart' },
    'terraform':    { icon: '🏗️', label: 'Terraform' },
  };
  const sourceMeta = sourceType ? SOURCE_META[sourceType] : null;

  return (
    <div className="ao-root">

      {/* IaC / Container source-type banner */}
      {sourceMeta && sourceType !== 'repository' && sourceType !== 'upload' && (
        <div className="iac-source-banner">
          <span className="iac-source-icon">{sourceMeta.icon}</span>
          <div>
            <span className="iac-source-label">{sourceMeta.label}</span>
            {sourceValue && <span className="iac-source-value">{sourceValue}</span>}
          </div>
          <span className="iac-source-note">SBOM generated from {sourceMeta.label}</span>
        </div>
      )}

      {/* Risk Hero */}
      <div className="ao-hero">
        <span className="ao-hero-label">{t('analysis.riskScore')}</span>
        {riskScore != null ? (
          <>
            <span className={`ao-hero-score ${color}`}>{riskScore}</span>
            <div className="ao-hero-bar-wrap">
              <div className={`ao-hero-bar ${color}`} style={{ width: `${riskScore}%` }} />
            </div>
            <span className="ao-hero-sub">
              {color === 'green' && t('analysis.riskLow')}
              {color === 'amber' && t('analysis.riskMedium')}
              {color === 'red'   && t('analysis.riskHigh')}
            </span>
          </>
        ) : (
          <>
            <Skeleton width={80} height={48} borderRadius={8} />
            <div className="ao-hero-bar-wrap">
              <Skeleton width="100%" height="100%" borderRadius={999} />
            </div>
          </>
        )}
      </div>

      {/* Pipeline status bar */}
      <div className="ao-pipeline">
        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--bs-secondary-color)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '0.25rem' }}>
          {t('analysis.analysesLabel')}
        </span>
        {pipelineSteps.map(step => (
          <PipelineStep
            key={step.key}
            label={step.label}
            status={stepStatus(step.key)}
            errorCount={states[step.key]?.error}
          />
        ))}
        <button
          className="ce-btn ce-btn--secondary ce-btn--sm"
          style={{ marginLeft: 'auto' }}
          onClick={runAll}
          title={t('analysis.rerunAll')}
        >
          🔄 {t('analysis.rerunAll')}
        </button>
      </div>

      {failedSteps.length > 0 && (
        <div className="alert alert-warning py-2 px-3">
          <div className="fw-semibold small mb-1">Some analysis results could not be loaded.</div>
          <ul className="small mb-0 ps-3">
            {failedSteps.map((step) => (
              <li key={step.key}>
                <strong>{step.label}:</strong> {step.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Summary cards grid */}
      <div className="ao-grid">
        <SecurityCard
          data={states.vulns.data}
          loading={states.vulns.loading}
          onNavigate={navigate}
        />
        <HealthCard
          data={states.health.data}
          loading={states.health.loading}
          onNavigate={navigate}
        />
        <LicenseCard
          data={states.licenses.data}
          loading={states.licenses.loading}
          onNavigate={navigate}
        />
        <QualityCard
          data={states.quality.data}
          loading={states.quality.loading}
          onNavigate={navigate}
        />
        <ComplianceCard
          data={states.compliance.data}
          loading={states.compliance.loading}
          onNavigate={navigate}
        />
        <RemediationCard
          data={remediationData}
          loading={remediationLoading}
          onNavigate={navigate}
        />
        <QuickActionsCard
          repoName={repoName}
          sbomId={sbomId}
          onNavigate={navigate}
        />
      </div>

    </div>
  );
}

export default AnalysisOverview;
